import type { CurrencyCode } from './model';

/**
 * Display helpers for amounts in major units. Amounts carry at most two
 * decimals (JPY included), so that is the finest precision worth showing.
 */

/** Minor digits the currency normally uses (JPY 0, GBP 2); 2 when unknown. */
export function currencyFractionDigits(currency: CurrencyCode): number {
  try {
    return (
      new Intl.NumberFormat('en', {
        style: 'currency',
        currency
      }).resolvedOptions().maximumFractionDigits ?? 2
    );
  } catch {
    return 2;
  }
}

/** Whole amounts without decimals ("850"), anything with cents with two. */
function displayed(amount: number): { value: number; digits: 0 | 2 } {
  const hundredths = Math.round(amount * 100);
  return {
    value: hundredths / 100,
    digits: hundredths % 100 === 0 ? 0 : 2
  };
}

export type FormatMoneyAmountOptions = {
  /**
   * `auto` (default): decimals only when there are cents, `850` / `850.50`.
   * `currency`: always the currency's own digits, for columns of figures
   * that should line up (`850.00`, `127.50`; JPY `12,000`).
   */
  fractionDigits: 'auto' | { currency: CurrencyCode };
};

/** `1250.5` → `1,250.50`, `850` → `850` (grouping follows the locale). */
export function formatMoneyAmount(
  amount: number,
  locale: string,
  options: FormatMoneyAmountOptions = { fractionDigits: 'auto' }
): string {
  const { value, digits } = displayed(amount);
  const shown =
    options.fractionDigits === 'auto'
      ? digits
      : Math.max(
          digits === 0 ? 0 : 2,
          Math.min(2, currencyFractionDigits(options.fractionDigits.currency))
        );
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: shown,
    maximumFractionDigits: shown
  }).format(value);
}

/**
 * The amount as a screen reader should say it: `850 Canadian dollars`
 * rather than "C A D 850".
 */
export function spokenMoney(
  amount: number,
  currency: CurrencyCode,
  locale: string
): string {
  const { value, digits } = displayed(amount);
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'name',
      minimumFractionDigits: digits,
      maximumFractionDigits: digits
    }).format(value);
  } catch {
    return `${formatMoneyAmount(amount, locale)} ${currency}`;
  }
}
